import React, { useState } from 'react';
import {
    Box, Button, Typography, Paper, List, ListItem, ListItemText, IconButton,
    LinearProgress, Alert, Snackbar, Container, RadioGroup, FormControlLabel,
    Radio, FormControl, FormLabel, TextField,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AuthService from '../Auth/AuthService';

const DropZone = styled(Paper)(({ theme }) => ({
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: theme.palette.background.default,
    transition: 'border 0.2s ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
    },
}));

const FileUploadPage = ({ Id, Type, onSuccess }) => {
    const [fileType, setFileType] = useState('photo');
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editName, setEditName] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const token = AuthService.tokenInfo();
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const getAcceptedTypes = () => {
        if (fileType === 'photo') {
            return 'image/jpeg,image/png';
        } else {
            return 'application/pdf';
        }
    };

    const isValidFile = (file) => {
        if (fileType === 'photo') {
            return file.type.startsWith('image/');
        } else {
            return file.type === 'application/pdf';
        }
    };

    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        addFiles(selectedFiles);
        event.target.value = null;
    };

    const addFiles = (newFiles) => {
        const validFiles = newFiles.filter(isValidFile);
        if (validFiles.length !== newFiles.length) {
            setSnackbar({
                open: true,
                message: `Only ${fileType === 'photo' ? 'image' : 'PDF'} files are allowed`,
                severity: 'warning',
            });
        }

        const filesWithMeta = validFiles.map((file) => ({
            originalFile: file,
            name: file.name,
            renamedFileName: `${fileType}_${Id}${file.name.substring(file.name.lastIndexOf('.'))}`,
        }));

        const newFilesMeta = filesWithMeta.map((fileMeta) => ({
            ...fileMeta,
            renamedFile: new File(
                [fileMeta.originalFile],
                fileMeta.renamedFileName,
                {
                    type: fileMeta.originalFile.type,
                    lastModified: fileMeta.originalFile.lastModified,
                }
            ),
        }));

        setFiles((prev) => [...prev, ...newFilesMeta]);

        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(validFiles[0]);
    };

    const removeFile = (indexToRemove) => {
        setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
        if (editingIndex === indexToRemove) {
            setEditingIndex(null);
        }
    };

    // Start editing a file name
    const startEditing = (index, currentName) => {
        setEditingIndex(index);
        setEditName(currentName);
    };

    // Cancel editing
    const cancelEditing = () => {
        setEditingIndex(null);
        setEditName('');
    };

    // Save renamed file
    const saveRename = (index) => {
        const fileMeta = files[index];
        if (!editName.trim()) {
            setSnackbar({
                open: true,
                message: 'File name cannot be empty',
                severity: 'error',
            });
            return;
        }

        const originalExt = fileMeta.originalFile.name.split('.').pop();
        let newName = editName.trim();
        if (!newName.endsWith(`.${originalExt}`)) {
            newName = `${newName}.${originalExt}`;
        }

        const renamedFile = new File([fileMeta.originalFile], newName, {
            type: fileMeta.originalFile.type,
        });

        const updatedFiles = [...files];
        updatedFiles[index] = {
            ...fileMeta,
            name: newName,
            originalFile: renamedFile,
        };
        setFiles(updatedFiles);
        setEditingIndex(null);
        setEditName('');

        setSnackbar({
            open: true,
            message: `Renamed to "${newName}"`,
            severity: 'success',
        });
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            setSnackbar({
                open: true,
                message: `Please select at least one ${fileType === 'photo' ? 'image' : 'PDF'} file`,
                severity: 'warning',
            });
            return;
        }
        console.log(files);
        if (!token?.mas_id) {
            setSnackbar({
                open: true,
                message: "Unable to find your school information. Please log out and sign in again.",
                severity: "warning",
            });
            return;
        }
        setUploading(true);
        const formData = new FormData();

        files.forEach((fileMeta) => { const file = fileMeta.renamedFile instanceof File ? fileMeta.renamedFile : fileMeta.originalFile; formData.append("file", file); });

        const dir = [token?.mas_id, Id, Type].filter(Boolean).join('/');
        formData.append('dir', dir);
        formData.append('type', fileType);

        try {
            const url = AuthService.pathUrl('POST', '/files/pdf/upload')
            const res = await AuthService.restPostFormData(url, formData)
            console.log('Upload success:', res)
            if (res !== undefined) {
                setSnackbar({
                    open: true,
                    message: `Successfully uploaded ${files.length} ${fileType === 'photo' ? 'image(s)' : 'PDF(s)'}`,
                    severity: 'success',
                });
                onSuccess(res, fileType);
                setFiles([]);
            }
        } catch (error) {
            console.error('Upload error:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Upload failed. Please try again.',
                severity: 'error',
            });
        } finally {
            setUploading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const droppedFiles = Array.from(event.dataTransfer.files);
        addFiles(droppedFiles);
    };

    const handleFileTypeChange = (event) => {
        setFileType(event.target.value);
        setFiles([]);
        setEditingIndex(null);
    };

    const getDisplayFileName = (fileMeta) => {
        return fileMeta.name || fileMeta.originalFile.name;
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* <Typography variant="h4" component="h1" gutterBottom align="center">
                Upload Files
            </Typography> */}

            <FormControl component="fieldset" sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* <FormLabel component="legend">Select file type</FormLabel> */}
                <RadioGroup row value={fileType} onChange={handleFileTypeChange}>
                    <FormControlLabel value="photo" control={<Radio />} label="Photo (Images)" />
                    <FormControlLabel value="pdf" control={<Radio />} label="PDF Document" />
                </RadioGroup>
            </FormControl>

            <DropZone
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input').click()}
                sx={{ mb: 3 }}
            >
                <input
                    id="file-input"
                    type="file"
                    accept={getAcceptedTypes()}
                    // multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="textSecondary">
                    Drag & drop {fileType === 'photo' ? 'images' : 'PDF files'} here, or click to select
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    {fileType === 'photo'
                        ? 'Accepted: JPEG, PNG, GIF, WebP, BMP'
                        : 'Only PDF files are accepted'}
                </Typography>
            </DropZone>

            {files.length > 0 && (
                <Paper variant="outlined" sx={{ mb: 3 }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle1">Selected Files ({files.length})</Typography>
                    </Box>
                    <List dense>
                        {files.map((fileMeta, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    <Box>
                                        {editingIndex === index ? (
                                            <>
                                                <IconButton edge="end" onClick={() => saveRename(index)} sx={{ mr: 1 }}>
                                                    <SaveIcon />
                                                </IconButton>
                                                <IconButton edge="end" onClick={cancelEditing}>
                                                    <CloseIcon />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <IconButton edge="end" onClick={() => startEditing(index, getDisplayFileName(fileMeta))} sx={{ mr: 1 }}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton edge="end" onClick={() => removeFile(index)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </>
                                        )}
                                    </Box>
                                }
                            >
                                {fileMeta.originalFile.type === 'application/pdf' ? (
                                    <PdfIcon sx={{ mr: 2, color: 'error.main' }} />
                                ) : (<>
                                    <img
                                        src={previewUrl}
                                        alt="Img"
                                        style={{
                                            height: '80px', borderRadius: '8px', mr: 2,
                                            '&:hover': { border: '10px solid #a9dcf3' }
                                        }}
                                    />
                                    {/* <ImageIcon sx={{ mr: 2, color: 'primary.main' }} /> */}
                                </>)}
                                {editingIndex === index ? (
                                    <TextField
                                        size="small"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') saveRename(index);
                                            if (e.key === 'Escape') cancelEditing();
                                        }}
                                        sx={{ flexGrow: 1, mr: 2 }}
                                    />
                                ) : (
                                    <ListItemText
                                        primary={getDisplayFileName(fileMeta)}
                                        secondary={`${(fileMeta.originalFile.size / 1024).toFixed(2)} KB`}
                                    />
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={uploading || files.length === 0}
                    startIcon={<CloudUploadIcon />}
                >
                    {uploading ? 'Uploading...' : `Upload ${fileType === 'photo' ? 'Photos' : 'PDFs'}`}
                </Button>
            </Box>

            {uploading && <LinearProgress sx={{ mt: 2 }} />}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default FileUploadPage;